<template>
  <div class="bdx-manager-list">
    <AdvancedScrollList :items="items" :loading="isFetching">
      <template v-slot:header>
        <BDXStatusPendingManagerListHeader>
          <template v-slot:first>
            <b-form-checkbox
              class="select-all-checkbox m-0"
              v-if="!!items.length"
              v-model="selectAll"
              :disabled="isFetching"
            ></b-form-checkbox>
          </template>
        </BDXStatusPendingManagerListHeader>
      </template>

      <template v-slot:item="{ item }">
        <BDXStatusPendingManagerListItem
          :item="item"
          :class="{ 'bg-secondary font-weight-bold': activePreviewId === item.id }"
        >
          <template v-slot:preview>
            <a role="button" @click.prevent="() => $emit('preview:item', item)">
              <BaseIcon icon="eye" tag="i" :font-scale="0.9" />
            </a>
          </template>

          <template v-slot:first>
            <BaseCheckbox v-model="checkedItems" :checked-value="item.id" />
          </template>

          <template v-slot:toggle>
            <BaseSwitch v-model="toggledItems" :checked-value="item.id" />
          </template>
        </BDXStatusPendingManagerListItem>
      </template>

      <template v-slot:empty-message>No UMRs found.</template>
    </AdvancedScrollList>

    <BDXStatusPendingManagerListPagination
      v-model="pagination"
      :total="totalPages"
      @input="fetchItems"
    />
  </div>
</template>

<script>
import Vue from 'vue'
import {
  defineComponent,
  computed,
  ref,
  watch,
  inject,
  onMounted
} from '@vue/composition-api'
import { createVuexHelpers } from 'vue2-helpers'
import { Fragment } from 'vue-fragment'
import { omit } from 'lodash-es'

import { useTryCatch, useCurrentUser, useUserCompany } from '@composables'
import { LincolnGlobalUMRService } from '@services'

import { BaseCheckbox, BaseSwitch, BaseIcon } from '@components/base'
import { AdvancedScrollList } from '@components/advanced'

import { BDXStatusPendingManagerInjectionKey } from './BDXStatusPendingManager.constants'

import BDXStatusPendingManagerListHeader from './BDXStatusPendingManagerListHeader.vue'
import BDXStatusPendingManagerListItem from './BDXStatusPendingManagerListItem.vue'
import BDXStatusPendingManagerListPagination from './BDXStatusPendingManagerListPagination.vue'

export default defineComponent({
  name: 'BDXStatusPendingManagerList',
  components: {
    Fragment,

    BaseCheckbox,
    BaseSwitch,
    BaseIcon,

    AdvancedScrollList,

    BDXStatusPendingManagerListHeader,
    BDXStatusPendingManagerListItem,
    BDXStatusPendingManagerListPagination
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    activePreviewId: {
      type: Number,
      default: null
    }
  },
  emits: ['input', 'preview:item'],
  setup(_, { emit }) {
    const { useGetters } = createVuexHelpers()

    const { bdxFilters } = useGetters(['bdxFilters'])

    const { isSuperUser } = useCurrentUser()
    const { roleName: userCompanyRoleName, roleValue: userCompanyRoleValue } =
      useUserCompany()

    const { search, columnFilters } = inject(BDXStatusPendingManagerInjectionKey)

    // Checked items
    const checkedItems = ref([])

    watch(checkedItems, (newCheckedItems) => {
      toggledItems.value = newCheckedItems.filter((newCheckedItem) => {
        return toggledItems.value.includes(newCheckedItem)
      })

      selectAll.value = newCheckedItems.length === items.value.length
    })

    // Toggled items
    const toggledItems = ref([])

    watch(toggledItems, (newToggledItems) => {
      newToggledItems.forEach((item) => {
        const checkedItemIndex = checkedItems.value.indexOf(item)

        if (0 > checkedItemIndex) {
          checkedItems.value.push(item)
        }
      })
    })

    // Model value
    const modelValue = computed(() =>
      checkedItems.value.map((checkedItem) => ({
        id: checkedItem,
        bdx_status: toggledItems.value.includes(checkedItem)
      }))
    )

    watch(modelValue, (newModelValue) => emit('input', newModelValue))

    // Data fetching
    const isFetching = ref(false)

    const pagination = ref({
      page: 1,
      page_size: 100
    })
    const totalPages = ref(0)

    const items = ref([])

    const fetchItems = async () => {
      isFetching.value = true

      selectAll.value = false

      await useTryCatch(async () => {
        const companyRole = isSuperUser.value
          ? bdxFilters.value.companyRoleName
          : userCompanyRoleName.value
        const companyRoleValue = isSuperUser.value
          ? bdxFilters.value.companyRoleValue
          : userCompanyRoleValue.value

        const { data } = await LincolnGlobalUMRService.listInactive(
          companyRole,
          companyRoleValue,
          {
            search: search.value,
            ...columnFilters.value,
            page: pagination.value.page,
            page_size: pagination.value.page_size
          }
        )
        const { count, results } = data || {}

        isFetching.value = false

        items.value = results

        totalPages.value = count
      })

      isFetching.value = false
    }

    const filterPayload = (args, status, type) => {
      if (!status) args = omit(args, [`${type}_status`])

      return args
    }

    watch([search, columnFilters, bdxFilters], fetchItems, {
      deep: true
    })

    onMounted(fetchItems)

    onMounted(() => {
      Vue.prototype.$emitter.on('bdx-edit-form:success', async ({ status }) => {
        if (!status) return

        await fetchItems()
      })

      Vue.prototype.$emitter.on('files-uploaded', async ({ type, success }) => {
        if (!success || 'bdx' !== type) return

        await fetchItems()
      })
    })

    // Bulk selection
    const selectAll = ref(false)

    watch(selectAll, (newSelectAll) => {
      if (!newSelectAll) {
        checkedItems.value = []
      } else {
        const newCheckedItems = items.value.map((item) => item.id)

        checkedItems.value = newCheckedItems
      }
    })

    return {
      checkedItems,
      toggledItems,

      modelValue,

      isFetching,

      pagination,
      totalPages,

      items,
      fetchItems,

      selectAll,
      filterPayload
    }
  }
})
</script>

<style scoped>

.bdx-manager-list >>> .select-all-checkbox {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;
}

.bdx-manager-list >>> .select-all-checkbox > label {
  min-width: 20px;
  min-height: 20px;
  padding-left: 0;
  margin-bottom: 0;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.bdx-manager-list >>> .first-col .base-checkbox {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.bdx-manager-list >>> .first-col .custom-checkbox > label {
  padding-left: 0;
  margin-bottom: 0;
}

.bdx-manager-list > div {
  overflow-x: auto;
}
.bdx-manager-list >>> .list-group {
  min-width: 1000px;
}
.bdx-manager-list >>> .bdx-col {
  padding: 0 8px;
  white-space: nowrap;
  flex-shrink: 0;
}
.bdx-manager-list >>> .bdx-col.rc-col {
  width: 100px;
}
.bdx-manager-list >>> .bdx-col.cc-col {
  width: 160px;
}
.bdx-manager-list >>> .bdx-col.sn-col {
  width: 210px;
}
.bdx-manager-list >>> .bdx-col.lu-col {
  width: 160px;
}
.bdx-manager-list >>> .flex-grow-1 {
  flex: 1 1 auto;
  min-width: 180px;
  white-space: nowrap;
}
</style>



